import React from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { ContactGroupSource } from '../../../helpers/missionDashboardV2';

const blink = keyframes`0%,100%{opacity:1}50%{opacity:.3}`;

/** Pastille statut (`.pill`) : vert clignotant si actif, gris sinon. */
export const StatusPill = ({ active }: { active: boolean }) => {
  const { t } = useTranslation(['missionDashboard']);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '3px 10px',
        borderRadius: '100px',
        fontSize: '10.5px',
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        ...(active
          ? { background: dsV2.color.vertDim, color: dsV2.color.vert, border: `1px solid ${dsV2.color.vertBrd}` }
          : { background: dsV2.color.surface2, color: dsV2.color.t3, border: `1px solid ${dsV2.color.border}` }),
      }}
    >
      {active ? (
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: dsV2.color.vert,
            animation: `${blink} 2s ease infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      ) : null}
      {active
        ? t('statusPill.active', { ns: 'missionDashboard' })
        : t('statusPill.inactive', { ns: 'missionDashboard' })}
    </Box>
  );
};

/** Tag type de mission (`.tag-it`) — bleu plein. */
export const TypeTag = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: dsV2.radius.xs,
      background: dsV2.color.bleu,
      color: dsV2.color.blanc,
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.04em',
    }}
  >
    {children}
  </Box>
);

/** Badge de source d'un groupe de contacts (mission prioritaire / société fallback). */
export const SrcBadge = ({ source }: { source: ContactGroupSource }) => {
  const { t } = useTranslation(['missionDashboard']);
  if (source === 'society')
    return <ChipV2 label={t('srcBadge.society', { ns: 'missionDashboard' })} tone="warning" size="small" />;
  if (source === 'mission')
    return <ChipV2 label={t('srcBadge.mission', { ns: 'missionDashboard' })} tone="info" size="small" />;
  return <ChipV2 label={t('srcBadge.none', { ns: 'missionDashboard' })} tone="neutral" size="small" />;
};
